import { computed, nextTick, ref } from 'vue';
import SalesPropertyLookupsAPI from 'dashboard/api/salesPropertyLookups';

export function useSalesOnusAnalysis({ t, notify }) {
  const enabled = ref(false);
  const file = ref(null);
  const status = ref('idle');
  const message = ref('');
  const progress = ref(0);
  const token = ref('');
  let requestVersion = 0;

  const processing = computed(() => status.value === 'processing');

  const resetResult = () => {
    status.value = 'idle';
    message.value = '';
    progress.value = 0;
    token.value = '';
  };

  const setEnabled = value => {
    requestVersion += 1;
    enabled.value = Boolean(value);
    if (!enabled.value) file.value = null;
    resetResult();
  };

  const setResult = resultStatus => {
    status.value = resultStatus || 'unavailable';
    if (status.value === 'success') {
      message.value = t('SALES.CREATE.ONUS.COMPLETE');
    } else if (status.value === 'not_configured') {
      message.value = t('SALES.CREATE.ONUS.NOT_CONFIGURED');
    } else {
      message.value = t('SALES.CREATE.ONUS.ANALYSIS_ERROR');
    }
    progress.value = 100;
  };

  const updateUploadProgress = (event, version) => {
    if (version !== requestVersion || !event?.total) return;
    if (event.loaded >= event.total) {
      progress.value = 40;
      message.value = t('SALES.CREATE.ONUS.SCANNING');
    }
  };

  const setFile = async selectedFile => {
    requestVersion += 1;
    const version = requestVersion;
    file.value = selectedFile || null;
    resetResult();
    if (!file.value) return;

    status.value = 'processing';
    progress.value = 1;
    message.value = t('SALES.CREATE.ONUS.PREPARING');
    try {
      const response = await SalesPropertyLookupsAPI.analyzeOnus(
        file.value,
        event => updateUploadProgress(event, version)
      );
      if (version !== requestVersion) return;

      const result = response?.data?.data || {};
      token.value = result.token || '';
      if (result.status === 'success') {
        progress.value = 70;
        message.value = t('SALES.CREATE.ONUS.FILLING');
        await nextTick();
      }
      setResult(result.status);
    } catch (error) {
      if (version !== requestVersion) return;
      token.value = '';
      setResult('unavailable');
      notify(error?.response?.data?.message || message.value);
    }
  };

  const applyResult = resultStatus => {
    if (resultStatus && resultStatus !== 'success') token.value = '';
    setResult(resultStatus);
  };

  const failUpload = () => {
    token.value = '';
    status.value = 'unavailable';
    message.value = t('SALES.CREATE.ONUS.UPLOAD_ERROR');
    progress.value = 100;
  };

  return {
    applyResult,
    enabled,
    failUpload,
    file,
    message,
    processing,
    progress,
    setEnabled,
    setFile,
    status,
    token,
  };
}
